import type { CalendarEvent } from 'owa-calendar-types';
import type { DirectionalHint } from '@fluentui/react/lib/Callout';

export interface AttendeePanelDialogState {
    calendarEvent: CalendarEvent;
    target: HTMLElement;
    directionalHint?: DirectionalHint;
}

// Map of window objects to their callbacks, supporting multiple reading panes (main + popouts)
const callbackMap = new Map<Window, (state: AttendeePanelDialogState | null) => void>();

/**
 * Registers a callback to be invoked when showAttendeePanelDialog is called.
 * This allows components to manage the callout via React state instead of imperative rendering.
 * @param callback Function to call with the callout state, or null to hide
 * @param targetWindow The window object this callback is associated with (defaults to current window)
 */
export function registerAttendeePanelDialogCallback(
    callback: (state: AttendeePanelDialogState | null) => void,
    targetWindow?: Window
): void {
    const win = targetWindow || window;
    callbackMap.set(win, callback);
}

/**
 * Unregisters a callback for a specific window
 * @param targetWindow The window object to unregister (defaults to current window)
 */
export function unregisterAttendeePanelDialogCallback(targetWindow?: Window): void {
    const win = targetWindow || window;
    callbackMap.delete(win);
}

/**
 * Shows the Attendee Panel as a callout anchored to a target element.
 * Automatically determines which window's provider to use based on the target element.
 * @param calendarEvent The calendar event to display attendee information for
 * @param target The HTML element to anchor the callout to (typically the clicked element)
 * @param directionalHint Optional direction hint for the callout positioning
 */
export function showAttendeePanelDialog(
    calendarEvent: CalendarEvent,
    target: HTMLElement,
    directionalHint?: DirectionalHint
): void {
    // Determine which window the target element belongs to
    const targetWindow = target.ownerDocument?.defaultView || window;
    const callback = callbackMap.get(targetWindow);

    if (callback) {
        callback({
            calendarEvent,
            target,
            directionalHint,
        });
    }
}

/**
 * Hides the currently displayed Attendee Panel callout in a specific window
 * @param targetWindow The window to hide the callout in (defaults to current window)
 */
export function hideAttendeePanelDialog(targetWindow?: Window): void {
    const win = targetWindow || window;
    const callback = callbackMap.get(win);

    if (callback) {
        callback(null);
    }
}
